import { AuthenticationManager } from '@frontend/authentication-v2';
import { useLocalStorageState } from '@frontend/common';
import { FullscreenSpinner } from '@frontend/components/common';
import { FunctionalityClient } from '@frontend/functionality/api';
import { Application, FeatureTarget, Functionality, FunctionalityType } from '@frontend/functionality/types';
import { useAccountRepository } from '@frontend/repository';
import { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { filterNivalizFunctionalities } from './filter-nivaliz-functionalities';

const MenuStoreContext = createContext<{
    isLoading: boolean;
    functionalities: Functionality[];
    applications: (Application | 'my')[];
    currentApp: Application | 'my' | null;
    previouslyUsedApps: { appType: Application | 'my'; lastUse: number }[];
    isValidLicense: (application: Application) => boolean;
    setCurrentApp: React.Dispatch<React.SetStateAction<Application | 'my' | null>>;
    getFunctionalitiesByApplication: (application: Application, type?: FunctionalityType) => Functionality[];
} | null>(null);

export const MenuStoreProvider = (props: { children: React.ReactNode }) => {
    const { currentAccount } = useAccountRepository();
    const [isLoading, setIsLoading] = useState(true);
    const [previouslyUsedApps, setPrevieuslyUsedApps] = useLocalStorageState<{ appType: Application | 'my'; lastUse: number }[]>('previeusly_used_apps', []); //apps should always be sorted on lastUse before storing
    const [selectedAppType, setSelectedAppType] = useLocalStorageState<string | null>(
        'selected_app',
        localStorage.getItem('selected_app') ? JSON.parse(localStorage.getItem('selected_app')!) : null
    );
    const [functionalities, setFunctionalities] = useState<Functionality[] | null>(null);
    const corporateFunctionalities = functionalities ? functionalities.filter((func) => func.type === FunctionalityType.CORPORATE) : [];
    const personalFunctionalities = functionalities ? functionalities.filter((func) => func.type === FunctionalityType.PERSONAL) : [];
    const [currentApp, setCurrentApp] = useState<Application | 'my' | null>(
        selectedAppType ? (selectedAppType as Application) : previouslyUsedApps.length > 0 ? previouslyUsedApps[0].appType : null
    );
    const [locationVersion, setLocationVersion] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        const handleLocationChange = () => setLocationVersion((v) => v + 1);
        window.addEventListener('local-storage:mobile-current-location', handleLocationChange);
        return () => window.removeEventListener('local-storage:mobile-current-location', handleLocationChange);
    }, []);

    useEffect(() => {
        AuthenticationManager.getInstance()
            .getTokenWhenReady()
            .then(async (token) => {
                if (!token) return;

                await FunctionalityClient.fetchFunctionalities({
                    account_id: currentAccount ?? token.account_id,
                    target: [FeatureTarget.MOBILE, FeatureTarget.API]
                })
                    .then((res) => {
                        setFunctionalities(filterNivalizFunctionalities(res.results));
                    })
                    .catch((error) => {
                        setFunctionalities([]);
                    });

                return;
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [currentAccount, locationVersion]);

    function isValidLicense(application: Application): boolean {
        return !!functionalities?.find((func) => func.application === application);
    }

    function getFunctionalitiesByApplication(application: Application, type?: FunctionalityType): Functionality[] {
        return functionalities?.filter((f) => f.application === application && (!type || f.type === type)) || [];
    }

    useEffect(() => {
        if (currentApp) {
            setSelectedAppType(currentApp);
            const previouslyUsedAppsCopy = [...previouslyUsedApps];
            const foundApp = previouslyUsedAppsCopy.find((app) => app.appType === currentApp);
            if (!foundApp) {
                previouslyUsedAppsCopy.push({ appType: currentApp, lastUse: Date.now() });
            } else foundApp.lastUse = Date.now();
            setPrevieuslyUsedApps(previouslyUsedAppsCopy.sort((a, b) => b.lastUse - a.lastUse));
            navigate(`${currentApp}/home`);
        } else {
            navigate(`/home`);
        }
    }, [currentApp]);

    useEffect(() => {
        if (functionalities) {
            if (currentApp) {
                if (currentApp === 'my') {
                    if (personalFunctionalities.length === 0) setCurrentApp(null);
                } else if (corporateFunctionalities.find((l) => l.application === currentApp) === undefined) {
                    if (previouslyUsedApps) {
                        const foundApp = previouslyUsedApps.find((app) => corporateFunctionalities.find((l) => l.application === app.appType));
                        if (foundApp) {
                            setCurrentApp(foundApp.appType);
                            return;
                        }
                    }
                    if (corporateFunctionalities.length > 0) {
                        setCurrentApp(corporateFunctionalities[0].application);
                    } else setCurrentApp(null);
                }
            } else {
                if (personalFunctionalities.length > 0) {
                    setCurrentApp('my');
                } else if (corporateFunctionalities.length > 0) {
                    setCurrentApp(corporateFunctionalities[0].application);
                }
            }
        }
    }, [functionalities, currentApp]);

    if (functionalities === null)
        return (
            <FullscreenSpinner
                screenClassName='bg-background-surface'
                spinner={{ className: 'text-brand-light fill-brand-primary size-16' }}
            />
        );

    const reducedCorporateApplications = corporateFunctionalities.reduce((apps, func) => {
        if (!apps.includes(func.application)) {
            apps.push(func.application);
        }
        return apps;
    }, [] as Application[]);

    return (
        <MenuStoreContext.Provider
            value={{
                isLoading,
                currentApp,
                applications: [...((personalFunctionalities.length > 0 ? ['my'] : []) as (Application | 'my')[]), ...reducedCorporateApplications],
                functionalities,
                previouslyUsedApps,
                isValidLicense,
                setCurrentApp,
                getFunctionalitiesByApplication
            }}>
            {props.children}
        </MenuStoreContext.Provider>
    );
};

export const useMenuRepository = () => {
    const context = useContext(MenuStoreContext);
    if (!context) {
        throw new Error('useMenuRepository must be used within an MenuStoreProvider');
    }
    return context;
};
